import { useEffect, useRef } from 'react';
import Lenis from '@studio-freight/lenis';

/**
 * Smooth scrolling (Lenis) for full-page marketing layouts, mirroring the
 * setup on the start page. Returns a `scrollTo(target)` helper where `target`
 * is an element id or 'top'; it falls back to native scrolling when Lenis is
 * not (yet) running.
 *
 * @param {{ duration?: number, offset?: number }} [options]
 * @returns {(target: string) => void}
 */
const useLenisScroll = ({ duration = 1.4, offset = -70 } = {}) => {
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration });
    lenisRef.current = lenis;
    let raf;

    function frame(time) {
      lenis.raf(time);
      raf = requestAnimationFrame(frame);
    }

    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [duration]);

  return (target) => {
    if (target === 'top') {
      if (lenisRef.current) {
        lenisRef.current.scrollTo(0, { duration: 1.6 });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    const el = document.getElementById(target);
    if (!el) return;
    if (lenisRef.current) {
      lenisRef.current.scrollTo(el, { offset, duration: 1.6 });
    } else {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };
};

export default useLenisScroll;
